import { WixPatternsContainer } from './WixPatternsContainer';
import { ModalLayoutProps } from '../services';
import { OnNavigationCallback } from '../model';

export class BeforeUnloadCompatState {
  readonly container: WixPatternsContainer;

  constructor(container: WixPatternsContainer) {
    this.container = container;
  }

  _onNavigation: OnNavigationCallback = async (pause) => {
    const { onNavigationModal, translate: t } = this.container;
    const modalPropsList = this._getBeforeUnloadListeners();

    if (!modalPropsList.length) {
      return;
    }

    const { resume } = pause();

    const state = {
      didCancel: false,
      confirmationsCount: 0,
    };

    for (const onNavigationModalProps of modalPropsList) {
      await new Promise<void>((resolve) => {
        onNavigationModal.modal.events.once('close', () => resolve());
        onNavigationModal.open({
          ...onNavigationModalProps,
          onConfirm: () => {
            state.confirmationsCount++;
            onNavigationModalProps?.onConfirm?.();
          },
          onClose: () => {
            state.didCancel = true;
          },
          primaryButtonText: t('cairo.dragAndDrop.leaveSite.modal.leave.CTA'),
          secondaryButtonText: t('cairo.cancel.button'),
        });
      });

      // Skip rest of the modals when the user decides to cancel the navigation
      if (state.didCancel) {
        break;
      }
    }

    // Resume navigation after all modals confirmed
    if (state.confirmationsCount === modalPropsList.length) {
      modalPropsList.forEach(({ onResume }) => onResume?.());
      resume();
    }
  };

  _getBeforeUnloadListeners() {
    return this.container.events
      .listeners('navigation')
      .map((l) => l())
      .filter((p): p is ModalLayoutProps => p != null);
  }

  init() {
    const { onNavigation, onBeforeUnload } = this.container;

    // In GizaProvider & BM that migrated to Giza, `onBeforeUnload` will be available (not possible to customize the modal)
    if (onBeforeUnload) {
      const onBeforeUnloadSubscription = onBeforeUnload((event) => {
        if (!this._getBeforeUnloadListeners().length) {
          return;
        }

        event.preventDefault();
      });

      return () => {
        onBeforeUnloadSubscription.remove();
      };
    }

    // In BM that didn't migrate to Giza, `onBeforeUnload` will be `undefined` due to `usePlatformApiCompat`
    // `onNavigation` will be available (possible to customize the modal)
    if (onNavigation) {
      const unloadDialog = (e: BeforeUnloadEvent) => {
        if (!this._getBeforeUnloadListeners().length) {
          return;
        }
        e.preventDefault();
        e.returnValue = '';
      };

      window.addEventListener('beforeunload', unloadDialog);
      const onNavigationSubscription = onNavigation(this._onNavigation);

      return () => {
        window.removeEventListener('beforeunload', unloadDialog);
        onNavigationSubscription.remove();
      };
    }

    return;
  }
}
